import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { SankeyAuto } from "../charts-motion/sankey";
import { withFigureIO } from "../components/figure";
import { useConfig } from "../config";
import { metadata } from "../data/data_finance_aid_fig_AID_FLOWS_GROUPED";
import { datasets } from "../data/data_finance_aid_fig_AID_FLOWS_GROUPED_datasets";
import { type ChartRenderer, getAidFlowName, mkGemDataDecoder } from "../domain";
import * as M from "../materials";
import { io } from "../prelude";
import { computeSankeyNodes, type NodeGroup } from "./AID_FLOWS";

export * from "../data/data_finance_aid_fig_AID_FLOWS_GROUPED";

export const Data = mkGemDataDecoder([], {
  donor: io.string,
  flow: io.number,
  recipient: io.string,
  channel: io.string,
  level: io.string,
});

export type Data = io.TypeOf<typeof Data>;

export const Chart = ({ data }: ChartRenderer<Data>) => {
  const { aidflows } = useConfig();
  const intl = useIntl();

  const nodeGroups: NodeGroup<Data[number]>[] = React.useMemo(
    () => [
      {
        nodes: "donor",
        depth: 0,
        label: intl.formatMessage(defineMessage({ id: "Donors", defaultMessage: "Donors" })),
        category: "donors",
      },
      {
        nodes: "channel",
        depth: 1,
        label: intl.formatMessage(defineMessage({ id: "Channels", defaultMessage: "Channels" })),
        category: "channels",
      },
      {
        nodes: "recipient",
        depth: 2,
        label: intl.formatMessage(defineMessage({ id: "Recipients", defaultMessage: "Recipients" })),
        category: "recipients",
      },
      {
        nodes: "level",
        depth: 3,
        label: intl.formatMessage(defineMessage({ id: "Education levels", defaultMessage: "Education levels" })),
        category: "education_levels",
      },
    ],
    [intl],
  );

  const colorPalettes = React.useMemo(
    () => [
      {
        label: intl.formatMessage(defineMessage({ id: "Donors", defaultMessage: "Donors" })),
        palette: M.q06Palette,
        id: "donors",
      },
      {
        label: intl.formatMessage(defineMessage({ id: "Channels", defaultMessage: "Channels" })),
        palette: M.q05Palette,
        id: "channels",
      },
      {
        label: intl.formatMessage(defineMessage({ id: "Recipients", defaultMessage: "Recipients" })),
        palette: M.q02Palette,
        id: "recipients",
      },
      {
        label: intl.formatMessage(defineMessage({ id: "Education levels", defaultMessage: "Education levels" })),
        palette: M.q10Palette,
        id: "education_levels",
      },
    ],
    [intl],
  );

  const sankeyData = React.useMemo(() => {
    const localizedData = data.map((row) => ({
      ...row,
      donorId: row.donor,
      donor: getAidFlowName(aidflows, "donor", row.donor),
      recipientId: row.recipient,
      recipient: getAidFlowName(aidflows, "recipient", row.recipient),
      levelId: row.level,
      level: getAidFlowName(aidflows, "level", row.level),
      channelId: row.channel,
      channel: getAidFlowName(aidflows, "channel", row.channel),
    }));
    return computeSankeyNodes(localizedData, nodeGroups);
  }, [aidflows, data, nodeGroups]);

  return (
    <SankeyAuto data={sankeyData} height={M.chartHeight.l} colorPalettes={colorPalettes} nodeGroups={nodeGroups} />
  );
};

export default withFigureIO({
  url: new URL("../data/data_finance_aid_fig_AID_FLOWS_GROUPED.json", import.meta.url).href,
  csv: new URL("../data/data_finance_aid_fig_AID_FLOWS_GROUPED.zip", import.meta.url).href,
  xlsx: new URL("../data/data_finance_aid_fig_AID_FLOWS_GROUPED.xlsx", import.meta.url).href,
  metadata,
  Data,
  Chart,
  datasets,
});
